import { useState } from "react"
import { CheckCircle2, CircleDashed, Factory, Package } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import type {
  CouplingLoadView,
  PlannedLotRow,
  UsinageDetail,
  UsinageLotRow,
} from "@/features/milling/api/types"
import { FabricateLotPlannedDialog } from "@/features/milling/components/fabricate-lot-planned-dialog"
import { formatNumber, kgToMt } from "@/lib/format"
import { cn } from "@/lib/utils"

interface FabricationListProps {
  usinage: UsinageDetail
  // The same flattened load/QDL list the matrix table uses. Lets each
  // Lot Planned row resolve its source cells back to a human-readable
  // identity (cooperative name for LOADs, lot number for QDLs).
  loads: CouplingLoadView[]
}

// Step 2 list view. One row per Lot Planned with status pill, source
// composition chips, planned vs actual metrics, and a "Fabriquer"
// button that opens the fabrication dialog for the row.
//
// Rendered whenever the usinage is in COUPLAGE_PLANNED or FABRICATION;
// COMPLETED renders a read-only summary elsewhere.
export function FabricationList({ usinage, loads }: FabricationListProps) {
  const { t } = useTranslation("milling")
  const [activePlan, setActivePlan] = useState<PlannedLotRow | null>(null)

  const planned = usinage.lotsPlanned ?? []
  const lotsById = new Map<number, UsinageLotRow>()
  for (const l of usinage.lots ?? []) lotsById.set(l.id, l)
  const doneCount = planned.filter(
    (p) => p.status !== "PENDING_FABRICATION",
  ).length

  if (planned.length === 0) {
    return (
      <Card className="flex flex-col items-center gap-3 p-10 text-center text-sm text-muted-foreground">
        <Factory className="size-6" />
        {t("fabricationList.empty")}
      </Card>
    )
  }

  return (
    <>
      <Card className="gap-0 overflow-hidden p-0">
        <div className="border-b bg-muted/40 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold tracking-tight">
              {t("fabricationList.title")}
            </h3>
            <span className="text-xs text-muted-foreground tabular-nums">
              {t("fabricationList.progress", {
                done: doneCount,
                total: planned.length,
              })}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("fabricationList.subtitle")}
          </p>
        </div>
        <ul className="divide-y">
          {planned.map((p) => {
            const lot = p.fabricatedLotId
              ? lotsById.get(p.fabricatedLotId)
              : undefined
            return (
              <li
                key={p.id}
                className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-start"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{p.label}</span>
                    <StatusPill plan={p} lot={lot} />
                  </div>

                  {/* Source breakdown — each chip names a chargement
                      / QDL and the bag count routed from it into this
                      Lot Planned. Lets the operator audit composition
                      without opening the matrix table below. */}
                  <SourceChips cells={p.cells} loads={loads} />

                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground tabular-nums">
                    {lot ? (
                      // Fabricated: surface the planned → actual delta
                      // (e.g. "410 → 385 bags") so the milling loss
                      // is visible per lot. Weight delta likewise.
                      <>
                        <span>
                          <span className="text-muted-foreground/70 line-through">
                            {p.plannedBags}
                          </span>
                          <span className="mx-1">→</span>
                          <span className="font-semibold text-foreground">
                            {t("fabricationList.actualBags", {
                              bags: lot.bags,
                            })}
                          </span>
                        </span>
                        <span>·</span>
                        <span>
                          <span className="text-muted-foreground/70 line-through">
                            {formatNumber(kgToMt(p.estimatedWeightKg), 3)}
                          </span>
                          <span className="mx-1">→</span>
                          <span className="font-semibold text-foreground">
                            {t("fabricationList.actualMt", {
                              mt: formatNumber(kgToMt(lot.netKg), 3),
                            })}
                          </span>
                        </span>
                      </>
                    ) : (
                      // Pending: just the plan.
                      <>
                        <span>
                          {t("fabricationList.plannedBags", {
                            bags: p.plannedBags,
                          })}
                        </span>
                        <span>·</span>
                        <span>
                          {t("fabricationList.estimatedMt", {
                            mt: formatNumber(kgToMt(p.estimatedWeightKg), 3),
                          })}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex flex-shrink-0 items-center gap-2 sm:self-center">
                  {p.status === "PENDING_FABRICATION" ? (
                    <Button size="sm" onClick={() => setActivePlan(p)}>
                      <Package className="size-4" />
                      {t("fabricationList.fabricate")}
                    </Button>
                  ) : lot ? (
                    <Badge
                      variant="outline"
                      className="font-mono text-[11px]"
                    >
                      {lot.lotNumber}
                    </Badge>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ul>
      </Card>

      {activePlan ? (
        <FabricateLotPlannedDialog
          open
          onOpenChange={(o) => {
            if (!o) setActivePlan(null)
          }}
          usinageId={usinage.id}
          plan={activePlan}
        />
      ) : null}
    </>
  )
}

function StatusPill({
  plan,
  lot,
}: {
  plan: PlannedLotRow
  lot?: UsinageLotRow
}) {
  const { t } = useTranslation("milling")

  if (plan.status === "PENDING_FABRICATION") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase",
          "bg-muted text-muted-foreground",
        )}
      >
        <CircleDashed className="size-3" />
        {t("fabricationList.statusPending")}
      </span>
    )
  }

  const isLot = plan.status === "FABRICATED_AS_LOT"
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium tracking-wide uppercase",
        isLot
          ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
          : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
      )}
    >
      <CheckCircle2 className="size-3" />
      {isLot
        ? t("fabricationList.statusLot", {
            lotNumber: lot?.lotNumber ?? "—",
          })
        : t("fabricationList.statusQueue", {
            lotNumber: lot?.lotNumber ?? "—",
          })}
    </span>
  )
}

// SourceChips renders one chip per cell in the Lot Planned's matrix
// row, naming the source (cooperative for LOADs, lot number for QDLs)
// and the bag count routed from it. QDL chips get an amber tint to
// match the badge used elsewhere in the UI.
function SourceChips({
  cells,
  loads,
}: {
  cells: PlannedLotRow["cells"]
  loads: CouplingLoadView[]
}) {
  const { t } = useTranslation("milling")
  if (cells.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-1">
      {cells.map((c, i) => {
        const kind = c.sourceKind ?? "LOAD"
        const ref = loads.find(
          (l) => l.loadId === c.loadId && l.sourceKind === kind,
        )
        const isQDL = kind === "QUEUE_LOT"
        // LOAD label preference: cooperative name → CCC (delivery
        // slip) → load number. QDL: lot number.
        const label = isQDL
          ? (ref?.loadNumber ?? `#${c.loadId}`)
          : (ref?.cooperativeName ??
              ref?.deliverySlip ??
              ref?.loadNumber ??
              `#${c.loadId}`)
        return (
          <Badge
            key={`${kind}-${c.loadId}-${i}`}
            variant={isQDL ? "secondary" : "outline"}
            className={cn(
              "text-[10px] font-normal tabular-nums",
              isQDL &&
                "bg-amber-500/10 text-amber-700 dark:text-amber-300",
            )}
          >
            {isQDL ? (
              <span className="mr-1 font-semibold uppercase tracking-wide">
                {t("fabricationList.sourceQdlPrefix")}
              </span>
            ) : null}
            <span className="max-w-[140px] truncate">{label}</span>
            <span className="ml-1 text-muted-foreground">·</span>
            <span className="ml-1">{c.bags}</span>
          </Badge>
        )
      })}
    </div>
  )
}
